import React from 'react';
import {IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonButton, IonIcon} from '@ionic/react';
import {chevronForwardOutline, settingsOutline} from 'ionicons/icons';
import './DashboardCard.css';

interface DashboardCardProps {
    title?: string;
    subtitle?: string;
    icon?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    onAction?: () => void;
    onSettings?: () => void;
    actionLabel?: string;
    loading?: boolean;
    className?: string;
}

/**
 * Card réutilisable pour widgets dashboard
 *
 * @param size - 'full' = pleine largeur (2 colonnes), 'half' = moitié (1 colonne)
 * @param onAction - Callback pour bouton "Voir plus"
 * @param title - Titre affiché dans le header
 * @param subtitle - Sous-titre (ex: timestamp)
 * @param icon - Icône Ionic à afficher à côté du titre
 * @param children - Contenu principal (valeur + unité)
 * @param footer - Contenu footer optionnel (métadonnées)
 * @param actionLabel - Label du bouton action
 * @param className - Classes CSS additionnelles
 */
export const DashboardCard: React.FC<DashboardCardProps> = ({
                                                                title,
                                                                subtitle,
                                                                icon,
                                                                children,
                                                                footer,
                                                                onAction,
                                                                onSettings,
                                                                actionLabel = 'Voir plus',
                                                                className = ''
                                                            }) => {
    return (
        <IonCard
            className={`background-card ${className}`}
            style={{
                minHeight: '140px',
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
            {title && (
                <IonCardHeader className="pb-2 px-4 pt-3">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                            {icon && <IonIcon icon={icon} className="text-xl"/>}
                            <div className="flex flex-col">
                                <IonCardTitle className="text-xl font-semibold card-title">
                                    {title}
                                </IonCardTitle>
                                {subtitle && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {subtitle}
                  </span>
                                )}
                            </div>
                        </div>
                        {onSettings && (
                            <IonButton
                                fill="clear"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSettings();
                                }}
                                className="m-0 h-6 w-6 min-w-0"
                                style={{'--padding-start': '0', '--padding-end': '0'}}
                            >
                                <IonIcon icon={settingsOutline} slot="icon-only" className="text-sm text-gray-500"/>
                            </IonButton>
                        )}
                    </div>
                </IonCardHeader>
            )}

            <IonCardContent className="pt-2 px-4 flex-1">
                {children}
            </IonCardContent>

            {footer && (
                <div className="px-4 pb-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                    {footer}
                </div>
            )}

            {onAction && (
                <div className="px-4 pb-3 flex justify-end">
                    <IonButton
                        fill="clear"
                        size="small"
                        onClick={onAction}
                        className="see-more-button"
                    >
                        {actionLabel}
                        <IonIcon icon={chevronForwardOutline} slot="end"/>
                    </IonButton>
                </div>
            )}
        </IonCard>
    );
};